// Sidebar colour identity per role, drawn from the school crest (navy,
// maroon, gold). Roles are grouped into families rather than given one
// colour each: two dozen unrelated hues would drift off-brand, while a
// handful of crest-derived families still tells a user at a glance which
// workspace they are in.
//
// `deep` is the top of the sidebar; the gradient flows toward `light` as the
// pointer moves down it. Both must keep white text at >= 4.5:1 contrast, so
// don't lighten `light` past roughly maroon-600 / navy-500.

export type SidebarThemeFamily = 'finance' | 'leadership' | 'academic' | 'operations' | 'community'

export interface SidebarTheme {
  family: SidebarThemeFamily
  label: string
  deep: string
  light: string
  accent: string
}

const THEMES: Record<SidebarThemeFamily, Omit<SidebarTheme, 'family'>> = {
  // Dark maroon to crest maroon, gold highlights.
  finance: { label: 'Finance Office', deep: '#3d0b09', light: '#a92f24', accent: '#ebcd7c' },
  // Midnight to crest navy, gold highlights.
  leadership: { label: 'Leadership', deep: '#08111f', light: '#2f4d7d', accent: '#ebcd7c' },
  // Navy to a brighter navy, maroon-tint highlights.
  academic: { label: 'Academics', deep: '#0c1b33', light: '#456599', accent: '#eda49e' },
  // Wine: the crest's navy and maroon mixed.
  operations: { label: 'Operations', deep: '#1f0d1c', light: '#6e2a4a', accent: '#ebcd7c' },
  // The whole crest, navy flowing into maroon.
  community: { label: 'School Community', deep: '#0f1f3d', light: '#8c1d18', accent: '#ebcd7c' },
}

const ROLE_FAMILY: Record<string, SidebarThemeFamily> = {
  bursar: 'finance',
  cfo: 'finance',

  admin: 'leadership',
  director: 'leadership',
  principal: 'leadership',
  head_teacher: 'leadership',
  board_viewer: 'leadership',
  donor_viewer: 'leadership',

  director_of_studies: 'academic',
  class_teacher: 'academic',
  teacher: 'academic',
  librarian: 'academic',
  school_counsellor: 'academic',
  disciplinary_committee: 'academic',
  sports_master: 'academic',

  school_doctor: 'operations',
  transport_manager: 'operations',
  bus_driver: 'operations',
  hostel_matron: 'operations',
  hostel_custodian: 'operations',
  kitchen_manager: 'operations',
  cafeteria_manager: 'operations',
  uniform_manager: 'operations',
  inventory_manager: 'operations',

  parent: 'community',
  student: 'community',
}

export function getSidebarTheme(role: string | undefined): SidebarTheme {
  const family = (role && ROLE_FAMILY[role]) || 'leadership'
  return { family, ...THEMES[family] }
}
